import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

/**
 * תבנית לוחית — בסגנון לוחות פיזיים של בית כנסת.
 * רקע עץ כהה, טקסט זהב חקוק, מסגרת כפולה מרשימה.
 */

const WOOD = '#2c1810';
const WOOD_LIGHT = '#3d2518';
const GOLD = '#d4a94c';
const GOLD_MUTED = 'rgba(212, 169, 76, 0.6)';
const GOLD_DIM = 'rgba(212, 169, 76, 0.35)';
const IVORY = '#f5edd8';
const IVORY_MUTED = 'rgba(245, 237, 216, 0.55)';

const CATEGORY_LABELS = {
  tachanun: 'תחנון', hallel: 'הלל', prayerAdditions: 'תוספות תפילה',
  seasonalPrayers: 'תפילות עונתיות', seasonalTransitions: 'מעברים עונתיים',
  selichot: 'סליחות', weekdayPrayers: 'תפילות ימי חול',
  shabbatAdditions: 'תוספות שבת', motzaeiShabbat: 'מוצאי שבת',
  seasonalCustoms: 'מנהגים עונתיים', liturgy: 'פיוטים וקריאות',
  leyning: 'קריאת התורה', haftarah: 'הפטרה',
  mourningPeriods: 'ימי אבל ותענית', general: 'כללי',
};

// -- מפריד מתכתי ---------------------------------------------------------------
const MetalDivider = ({ wide = false }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    my: 0.6,
    width: wide ? '85%' : '65%',
    mx: 'auto',
  }}>
    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent, ${GOLD_DIM})` }} />
    <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: GOLD_DIM }} />
    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${GOLD_DIM}, transparent)` }} />
  </Box>
);

// -- כותרת קטגוריה -------------------------------------------------------------
const CategoryHeader = ({ label }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    justifyContent: 'center',
    mb: 0.4,
    mt: 0.75,
  }}>
    <Box sx={{ flex: 1, maxWidth: 45, height: '1px', background: `linear-gradient(90deg, transparent, ${GOLD_DIM})` }} />
    <Typography sx={{
      fontFamily: 'Frank Ruhl Libre, David, serif',
      fontSize: '0.55rem',
      color: GOLD_MUTED,
      letterSpacing: '0.12em',
      whiteSpace: 'nowrap',
      fontWeight: 600,
    }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, maxWidth: 45, height: '1px', background: `linear-gradient(90deg, ${GOLD_DIM}, transparent)` }} />
  </Box>
);

// -- פריט בודד — שורה חקוקה ----------------------------------------------------
const ItemRow = ({ item }) => {
  const isHigh = item.priority === 'high';

  return (
    <Box sx={{
      py: 0.5,
      px: 1.25,
      textAlign: 'center',
    }}>
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, David, serif',
        fontSize: isHigh ? '0.82rem' : '0.75rem',
        fontWeight: isHigh ? 700 : 500,
        color: isHigh ? GOLD : IVORY,
        lineHeight: 1.55,
        direction: 'rtl',
        textShadow: isHigh ? `0 0 8px rgba(212,169,76,0.3)` : 'none',
      }}>
        {item.label}
        {item.value ? ` — ${item.value}` : ''}
      </Typography>
      {item.detail && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.6rem',
          color: IVORY_MUTED,
          lineHeight: 1.4,
          direction: 'rtl',
        }}>
          {item.detail}
        </Typography>
      )}
    </Box>
  );
};

// -- עזר: קיבוץ לפי קטגוריה ---------------------------------------------------
function groupByCategory(items) {
  const map = {};
  items.forEach((item) => {
    const cat = item.category || 'general';
    if (!map[cat]) map[cat] = [];
    map[cat].push(item);
  });
  return Object.entries(map);
}

// -- יום lookahead בודד --------------------------------------------------------
const LookaheadDay = ({ dayData }) => (
  <Box sx={{ mt: 0.75 }}>
    <MetalDivider wide />
    <Typography sx={{
      fontFamily: 'Frank Ruhl Libre, David, serif',
      fontSize: '0.75rem',
      fontWeight: 700,
      color: GOLD_MUTED,
      textAlign: 'center',
      mb: 0.3,
      letterSpacing: '0.03em',
    }}>
      {dayData.hebrewDate?.hebrew || dayData.date}
    </Typography>
    {dayData.items.length === 0 ? (
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, David, serif',
        fontSize: '0.65rem',
        color: IVORY_MUTED,
        textAlign: 'center',
      }}>
        אין ענייני יום מיוחדים
      </Typography>
    ) : (
      groupByCategory(dayData.items).map(([cat, catItems]) => (
        <Box key={cat} sx={{ mb: 0.5 }}>
          <CategoryHeader label={CATEGORY_LABELS[cat] || cat} />
          {catItems.map((item, i) => (
            <ItemRow key={item.id || `${cat}-${i}`} item={item} />
          ))}
        </Box>
      ))
    )}
  </Box>
);

// == תבנית לוחית ===============================================================
const Plaque = ({
  today,
  items,
  lookaheadItems,
  lookaheadDays,
  loading,
  error,
  title,
  showTitle,
}) => {
  const hebrewDateStr = today?.hebrewDate?.hebrew || '';
  const grouped = groupByCategory(items || []);

  return (
    <Box sx={{
      background: `linear-gradient(170deg, ${WOOD_LIGHT} 0%, ${WOOD} 40%, #1e0f08 100%)`,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      direction: 'rtl',
      overflowY: 'auto',
      padding: '20px 18px',
      position: 'relative',
      // מסגרת חיצונית — "מסגרת עץ"
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 4,
        border: `2.5px solid ${GOLD}`,
        borderRadius: '3px',
        pointerEvents: 'none',
        opacity: 0.55,
        boxShadow: `inset 0 0 6px rgba(212,169,76,0.15)`,
      },
      // מסגרת פנימית
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 10,
        border: `1px solid ${GOLD}`,
        borderRadius: '2px',
        pointerEvents: 'none',
        opacity: 0.25,
      },
      '&::-webkit-scrollbar': { width: 3 },
      '&::-webkit-scrollbar-thumb': { background: `${GOLD}40`, borderRadius: 2 },
    }}>
      {/* כותרת */}
      {showTitle && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.72rem',
          color: GOLD,
          letterSpacing: '0.18em',
          mt: 0.5,
          position: 'relative',
          zIndex: 1,
          fontWeight: 700,
          textShadow: `0 0 12px rgba(212,169,76,0.25)`,
        }}>
          {title}
        </Typography>
      )}

      {/* תאריך עברי */}
      {hebrewDateStr && (
        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', width: '100%' }}>
          <MetalDivider wide />
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '1.05rem',
            fontWeight: 700,
            color: GOLD,
            letterSpacing: '0.06em',
            textShadow: `0 0 16px rgba(212,169,76,0.3)`,
          }}>
            {hebrewDateStr}
          </Typography>
          <MetalDivider wide />
        </Box>
      )}

      {/* loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, position: 'relative', zIndex: 1 }}>
          <CircularProgress size={18} sx={{ color: GOLD }} />
        </Box>
      )}

      {/* error */}
      {error && (
        <Typography sx={{
          fontSize: '0.68rem',
          color: '#e57373',
          textAlign: 'center',
          mb: 0.5,
          position: 'relative',
          zIndex: 1,
        }}>
          שגיאה בטעינה
        </Typography>
      )}

      {/* ריק */}
      {grouped.length === 0 && !loading && (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.78rem',
          color: IVORY_MUTED,
          textAlign: 'center',
          mt: 2,
          position: 'relative',
          zIndex: 1,
        }}>
          אין ענייני יום מיוחדים להיום
        </Typography>
      )}

      {/* פריטי היום לפי קטגוריות */}
      <Box sx={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
        {grouped.map(([cat, catItems], gi) => (
          <Box key={cat} sx={{ mb: 0.25 }}>
            <CategoryHeader label={CATEGORY_LABELS[cat] || cat} />
            {catItems.map((item, i) => (
              <ItemRow key={item.id || `${cat}-${i}`} item={item} />
            ))}
          </Box>
        ))}
      </Box>

      {/* ימי lookahead */}
      {lookaheadDays > 0 && lookaheadItems && lookaheadItems.length > 0 && (
        <Box sx={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
          {lookaheadItems.map((dayData, i) => (
            <LookaheadDay key={dayData.date || i} dayData={dayData} />
          ))}
        </Box>
      )}

      {/* סיום */}
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
        <MetalDivider />
      </Box>
    </Box>
  );
};

export default Plaque;
